import { useState } from 'react';
import { useIntl } from 'react-intl';

import { PAGE_SIZE } from '~/common/constants/page';
import { Box } from '~/eds/src/components/Box/Box';
import { Link } from '~/eds/src/components/Link/Link';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Text } from '~/eds/src/components/Text/Text';
import { Info } from '~/eds/src/modules/Info/Info';
import { Table } from '~/eds/src/modules/Table/Table';

import { AribaError, ConfigurationPanelProps } from './types';
import { useConfigurationInfos } from './useConfigurationInfos';

export const ConfigurationDetailPanel = ({
  config,
  errors,
  isLoading,
  tab,
  onHide,
}: ConfigurationPanelProps) => {
  const intl = useIntl();

  const {
    id,
    allowedSyncFileTypes,
    createdBy,
    dateCreated,
    dateUpdated,
    evisortFolderPath,
    provider,
    providerOptions,
  } = config;

  type PaginationParams = {
    pageIndex: number;
  };

  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState(tab);

  const columns = [
    {
      title: intl.formatMessage({
        id: 'ConfigurationDetailPanel.documentId',
        defaultMessage: 'Document Id',
        description: 'Column title for document ID',
      }),
      key: 'id',
      cellType: 'link',
      renderCell: (data: AribaError) => {
        return (
          <Link href={`${data.aribaUrl}?ID=${data.id}&realm=${data.realm}`}>
            {data.id}
          </Link>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'ConfigurationDetailPanel.evisortId',
        defaultMessage: 'Evisort Id',
        description: 'Column title for Evisort ID',
      }),
      key: 'evisortId',
      cellType: 'link',
      renderCell: (data: AribaError) => {
        if (data.evisortId) {
          return (
            <Link pathname={`/document/${data.evisortId}`} enableNewTab={true}>
              {data.evisortId}
            </Link>
          );
        } else {
          return (
            <Text>
              {intl.formatMessage({
                id: 'ConfigurationDetailPanel.noDocumentExists',
                defaultMessage: 'No document exists',
                description: 'Message when no document exists',
              })}
            </Text>
          );
        }
      },
    },
    {
      title: intl.formatMessage({
        id: 'ConfigurationDetailPanel.workspaceId',
        defaultMessage: 'Workspace Id',
        description: 'Column title for workspace ID',
      }),
      key: 'workspaceId',
      cellType: 'text',
      mapCellProps: (data: AribaError) => ({
        text: data.workspaceId,
      }),
    },
    {
      title: intl.formatMessage({
        id: 'ConfigurationDetailPanel.fileName',
        defaultMessage: 'Ariba File Name',
        description: 'Column title for file name',
      }),
      key: 'fileName',
      cellType: 'text',
      mapCellProps: (data: AribaError) => ({
        text: data.title,
      }),
    },
    {
      title: intl.formatMessage({
        id: 'ConfigurationDetailPanel.reason',
        defaultMessage: 'Reason',
        description: 'Column title for reason',
      }),
      key: 'status',
      cellType: 'text',
      mapCellProps: (data: AribaError) => ({
        text: data.status,
      }),
    },
    {
      title: intl.formatMessage({
        id: 'ConfigurationDetailPanel.timestamp',
        defaultMessage: 'Timestamp',
        description: 'Column title for timestamp',
      }),
      key: 'timestamp',
      cellType: 'datetime',
      mapCellProps: (data: AribaError) => ({
        datetime: data.timestamp,
      }),
    },
  ];

  const tabs = {
    selectedTab: activeTab,
    onSelectTab: setActiveTab,
    tabs: [
      {
        label: intl.formatMessage({
          id: 'ConfigurationDetailPanel.configurationTab',
          defaultMessage: 'Configuration',
          description: 'Label for configuration tab',
        }),
        value: 'config',
        panel: (
          <Box>
            <Info
              sections={[
                {
                  title: '',
                  infos: useConfigurationInfos({
                    id,
                    providerOptions,
                    createdBy,
                    evisortFolderPath,
                    allowedSyncFileTypes,
                    dateCreated,
                    dateUpdated,
                    provider,
                  }),
                },
              ]}
            />
          </Box>
        ),
      },
      {
        label: intl.formatMessage({
          id: 'ConfigurationDetailPanel.errorLogTab',
          defaultMessage: 'Error Log',
          description: 'Label for error log tab',
        }),
        value: 'errors',
        count: errors?.count,
        panel: (
          <Box sx={{ flex: 'auto' }}>
            <Table
              name="errors"
              columns={columns}
              data={
                errors?.results.slice(
                  (page - 1) * PAGE_SIZE,
                  page * PAGE_SIZE,
                ) || []
              }
              totalCount={errors?.count}
              isLoading={isLoading}
              options={{
                enableExportXlsx: false,
                enableManageColumns: false,
                enableSelectRows: false,
              }}
              state={{ pageIndex: page, pageSize: PAGE_SIZE }}
              onPaginate={({ pageIndex }: PaginationParams) => {
                setPage(pageIndex);
              }}
            />
          </Box>
        ),
      },
    ],
  };

  return (
    <Panel
      hidden={{
        placement: 'left',
        onHide,
      }}
      position="right"
      title={intl.formatMessage(
        {
          id: 'ConfigurationDetailPanel.panelTitle',
          defaultMessage: 'Configuration for: {folderPath}',
          description:
            'Title for configuration panel. Example: folderPath = "/contracts/drafts"',
        },
        { folderPath: evisortFolderPath },
      )}
      width="l"
      tabs={tabs}
    ></Panel>
  );
};
